import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _pick from "lodash/pick";
import { InMemoryCache } from 'apollo-cache-inmemory';
import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { RestLink } from 'apollo-link-rest';
import { useCallback } from 'react';
import { getToken } from '../auth';
import { READ_ONLY_ERROR_CODE, setReadOnlyMode } from '../core';
import { AppName, createLogger, transactionId } from '../logging';
import session from '../session';
import { PAGINATION_TOKEN, parsePagination } from './pagination';
const logger = createLogger(AppName.Common, 'gql');
export const DEFAULT_BASE_ENDPOINT = process.env.REACT_APP_BOWTIE_API_BASE_ENDPOINT || '';
const CUSTOM_ENDPOINT_KEY = 'custom-base-endpoint';
const validateCustomBaseEndpoint = endpoint => /localhost:\d+/.test(endpoint) || /([a-z0-9-]+\.)*api\.(dev|staging)\.bowtie\.com\.hk$/.test(endpoint);
const getBaseEndpoint = () => {
  if (process.env.REACT_APP_ENVIRONMENT !== 'production') {
    const customEndpointKey = CUSTOM_ENDPOINT_KEY;
    const customEndpoint = localStorage.getItem(customEndpointKey) || '';
    if (customEndpoint) {
      if (validateCustomBaseEndpoint(customEndpoint)) return customEndpoint;
      localStorage.removeItem(customEndpointKey);
    }
  }
  return DEFAULT_BASE_ENDPOINT;
};
export const BASE_ENDPOINT = getBaseEndpoint();
export const useSetCustomEndpoint = () => {
  const handleBaseEndpointChange = useCallback(newValue => {
    if (newValue) {
      localStorage.setItem(CUSTOM_ENDPOINT_KEY, newValue);
    } else {
      localStorage.removeItem(CUSTOM_ENDPOINT_KEY);
    }
    window.location.reload();
  }, []);
  return [getBaseEndpoint(), handleBaseEndpointChange];
};
export const apiOrigin = BASE_ENDPOINT.replace(/https?:\/\/(.*)/, '$1');
const getExtraHeaders = function () {
  var _ref = _asyncToGenerator(function* ({
    context
  }) {
    const token = yield getToken();
    const sessionConfig = session.config();
    const sessionId = session.getId();
    const acceptLanguage = yield context.acceptLanguage();
    const extraHeaders = {};
    if (token) extraHeaders.authorization = `Bearer ${token}`;
    if (acceptLanguage) extraHeaders['accept-language'] = acceptLanguage;
    if (transactionId) extraHeaders['x-transaction-id'] = transactionId;
    if (sessionId) extraHeaders[sessionConfig.header] = sessionId;
    return extraHeaders;
  });
  return function getExtraHeaders(_x) {
    return _ref.apply(this, arguments);
  };
}();
const rethrowServerSideError = (response, result, message) => {
  const error = new Error(message);
  error.response = response;
  error.statusCode = response.status;
  error.result = result;
  throw error;
};
const createRestLink = options => new RestLink({
  uri: options.uri || `${BASE_ENDPOINT}/customer`,
  endpoints: {
    root: BASE_ENDPOINT,
    partnership: `${BASE_ENDPOINT}/partnership`,
    customer: `${BASE_ENDPOINT}/customer`,
    merchant: `${BASE_ENDPOINT}/merchant`,
    advisor: `${BASE_ENDPOINT}/advisor`
  },
  headers: {
    'content-type': 'application/json'
  },
  bodySerializers: {
    multipartFormData: (data, headers) => {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        formData.append(key, value);
      });
      headers.delete('content-type');
      return {
        body: formData,
        headers
      };
    }
  },
  responseTransformer: function () {
    var _ref2 = _asyncToGenerator(function* (response, typeName) {
      if (response instanceof Response) {
        let parsed;
        const cloned = response.clone();
        try {
          parsed = yield cloned.clone().json();
        } catch {
          parsed = yield cloned.clone().text();
        }
        if (typeName !== null && typeName !== void 0 && typeName.startsWith(PAGINATION_TOKEN)) {
          const contentRange = response.headers.get('Content-Range') || '';
          const pagination = parsePagination(contentRange);
          return {
            results: parsed,
            pagination
          };
        }
        return parsed;
      }
      return response;
    });
    return function responseTransformer(_x2, _x3) {
      return _ref2.apply(this, arguments);
    };
  }(),
  customFetch: function () {
    var _ref3 = _asyncToGenerator(function* (requestUrl, requestParams) {
      const originalHeaders = {};
      if (requestParams.headers !== undefined) {
        if (requestParams.headers.constructor === Headers) {
          requestParams.headers.forEach((value, key) => {
            originalHeaders[key] = value;
          });
        } else {
          Object.entries(requestParams.headers).forEach(([key, value]) => {
            originalHeaders[key] = value;
          });
        }
      }
      const extraHeaders = yield getExtraHeaders(options);
      const headers = {
        ...originalHeaders,
        ...extraHeaders
      };
      const response = yield fetch(requestUrl, {
        ...requestParams,
        headers
      });
      if (options.errorOn404 && response.status === 404) {
        rethrowServerSideError(response, null, `Resource at ${requestUrl} not found.`);
      }
      return response;
    });
    return function customFetch(_x4, _x5) {
      return _ref3.apply(this, arguments);
    };
  }()
});
const errorLink = onError(({
  graphQLErrors,
  networkError,
  operation
}) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({
      message,
      locations,
      path
    }) => logger.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`));
  }
  if (networkError) {
    var _serverError$result, _operation$query$loc;
    const serverError = 'result' in networkError ? networkError : undefined;
    const result = serverError !== null && serverError !== void 0 && serverError.result ? JSON.stringify(serverError.result) : 'No response body';
    if ((serverError === null || serverError === void 0 ? void 0 : serverError.statusCode) === 503 && (serverError === null || serverError === void 0 ? void 0 : (_serverError$result = serverError.result) === null || _serverError$result === void 0 ? void 0 : _serverError$result.code) === READ_ONLY_ERROR_CODE) {
      setReadOnlyMode();
    }
    logger.log(`[Network error]: ${networkError.message}`, `[Response body]: ${result}`, (_operation$query$loc = operation.query.loc) === null || _operation$query$loc === void 0 ? void 0 : _operation$query$loc.source.body, operation.operationName, JSON.stringify(_pick(operation.variables, 'id')));
  }
});
const createClient = options => {
  const restLink = createRestLink(options);
  return new ApolloClient({
    link: ApolloLink.from([errorLink, restLink]),
    cache: new InMemoryCache()
  });
};
export default createClient;